import { useState, useEffect, useCallback, useRef } from 'react';
import Services from 'services';

interface UseReportingAuthTokenResult {
  token: string | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getToken: (forceRefresh?: boolean) => Promise<string>;
  /** Marks the token as invalid so next getToken() call will refresh */
  invalidateToken: () => void;
}

/**
 * Fetches the reporting auth token.
 *
 * WHAT: Fetches token for authenticating with the Reporting API.
 *
 * WHY: We need to obtain a bearer token for the Reporting API. We call the
 * RSOTF `/system/reporting/auth-token` endpoint.
 *
 * HOW: Call `Services.reportsNew.getAuthToken()` to fetch from backend.
 */
async function fetchReportingToken(): Promise<string> {
  const response = await Services.reportsCrs.getAuthToken();
  if (response && response.token) {
    return response.token;
  }

  throw new Error('Failed to fetch auth token');
}

/**
 * Hook for obtaining the Reporting API authentication token.
 *
 * WHAT: Returns the current auth token for the Reporting API.
 *
 * WHY: This hook encapsulates the auth token acquisition logic that was
 * previously handled by Redux sagas. It supports both environment variable
 * override (for development/demo) and fetching from the backend API.
 *
 * HOW: On mount, the hook checks for a hardcoded token env var first.
 * If not present, it calls the RSOTF API to get a token. The hook
 * provides a refresh function to re-fetch the token if needed.
 */
export function useReportingAuthToken(): UseReportingAuthTokenResult {
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const tokenRef = useRef<string | null>(null);
  const refreshPromiseRef = useRef<Promise<string> | null>(null);
  const shouldRefreshRef = useRef<boolean>(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const fetchedToken = await fetchReportingToken();
      if (mountedRef.current) {
        setToken(fetchedToken);
        tokenRef.current = fetchedToken;
      }
    } catch (err) {
      if (mountedRef.current) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch auth token';
        setError(errorMessage);
        tokenRef.current = null;
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Get the current token, refreshing if necessary.
   *
   * WHY: Provides a way for the SDK to get a fresh token on demand,
   * particularly useful when a 401 is detected.
   *
   * HOW: Returns cached token if available, unless shouldRefreshRef is set.
   * Deduplicates concurrent refresh requests.
   *
   * @param forceRefresh - If true, ignores cached token and fetches a new one
   */
  const getToken = useCallback(
    async (forceRefresh: boolean = false): Promise<string> => {
      // Check if we should force refresh (set by 401 detection or explicit request)
      const needsRefresh = forceRefresh || shouldRefreshRef.current;

      // If we have a valid token and not forcing refresh, return it
      if (tokenRef.current && !needsRefresh) {
        return tokenRef.current;
      }

      // Clear cached token and refresh flag
      if (needsRefresh) {
        tokenRef.current = null;
        shouldRefreshRef.current = false;
      }

      // If a refresh is already in progress, wait for it
      if (refreshPromiseRef.current) {
        return refreshPromiseRef.current;
      }

      // Start a new refresh
      refreshPromiseRef.current = (async () => {
        try {
          const freshToken = await fetchReportingToken();
          if (mountedRef.current) {
            setToken(freshToken);
            tokenRef.current = freshToken;
            setError(null);
          }
          return freshToken;
        } catch (err) {
          if (mountedRef.current) {
            const errorMessage =
              err instanceof Error ? err.message : 'Failed to fetch auth token';
            setError(errorMessage);
          }
          throw err;
        } finally {
          refreshPromiseRef.current = null;
          if (mountedRef.current) {
            setLoading(false);
          }
        }
      })();

      return refreshPromiseRef.current;
    },
    []
  );

  /**
   * Fetch token on mount.
   *
   * WHY: We need the token as soon as the reporting module loads.
   * HOW: Uses cleanup function to prevent state updates on unmounted component.
   */
  useEffect(() => {
    mountedRef.current = true;
    refresh();

    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  /**
   * Mark the current token as invalid.
   * Next call to getToken() will fetch a fresh token.
   */
  const invalidateToken = useCallback(() => {
    shouldRefreshRef.current = true;
  }, []);

  return {
    token,
    loading,
    error,
    refresh,
    getToken,
    invalidateToken
  };
}

export default useReportingAuthToken;
